import { isLeft } from 'fp-ts/Either'
import * as t from 'io-ts'
import { PathReporter } from 'io-ts/PathReporter'

import { CanaryStatus } from './types'

export { CanaryStatus } from './types'

export interface VersionMetadata {
  version: string | null
  canaryStatus: CanaryStatus
}

export const VERSION_FILE_NAME = 'version.json'

const CANARY_STATUS_HEADER = 'x-thyme-canary-status'

/**
 * Fetches the canary deployment status from the version file response headers.
 *
 */
export async function getCanaryStatus(): Promise<CanaryStatus> {
  const { canaryStatus } = await loadVersionMetadata()
  return canaryStatus
}

const VersionDecoder = t.type({
  version: t.string,
})

/**
 * Validator for version JSON.
 * @param arg
 */
function validateVersion(arg: any): string {
  const decoded = VersionDecoder.decode(arg)
  if (isLeft(decoded)) {
    throw new Error(
      `Application version is not valid: ${PathReporter.report(decoded).join(
        '\n'
      )}`
    )
  }
  return decoded.right.version
}

/**
 * Fetches both the app version and canary status in a single request.
 * @param filename
 */
export async function loadVersionMetadata(
  filename = VERSION_FILE_NAME
): Promise<VersionMetadata> {
  try {
    const response = await fetch(`/${filename}`)
    const status = response.headers.get(CANARY_STATUS_HEADER)
    const canaryStatus =
      status === CanaryStatus.Canary || status === CanaryStatus.Primary
        ? status
        : CanaryStatus.Unknown

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const versionObj = await response.json()
    return {
      version: validateVersion(versionObj),
      canaryStatus,
    }
  } catch (err: any) {
    console.error('Failed to fetch application version', err)
    return {
      version: null,
      canaryStatus: CanaryStatus.Unknown,
    }
  }
}
